import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityFieldList } from '../../../components/entity-detail/EntityFieldList.js';
import { EntityMetric } from '../../../components/entity-detail/EntityMetric.js';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatRelative,
  formatRelativeShort,
} from '../../../shared/format/entity-detail.js';
import type { DealListItem } from '../../deals/api/deals.client.js';
import type { NoteItem } from '../../notes/api/notes.client.js';
import type { TaskItem } from '../../tasks/api/tasks.client.js';
import type { CompanyDetail } from '../api/companies.client.js';
import type { CompanyHistoryItem } from '../hooks/useCompanyHistory.js';
import { isDealOpen } from './CompanyDealsTab.js';
import { companyWebsiteHref } from './CompanyDetailHeader.js';

export function isTaskOpen(task: TaskItem): boolean {
  return task.status !== 'done' && task.status !== 'cancelled';
}

export function isTaskOverdue(task: TaskItem, now = Date.now()): boolean {
  return isTaskOpen(task) && Boolean(task.dueAt) && Date.parse(task.dueAt!) < now;
}

function historyEvent(kind: string): string {
  if (kind.startsWith('company_')) return 'company';
  if (kind.startsWith('deal_')) return 'deal';
  if (kind.startsWith('contact_') || kind === 'interaction_logged') return 'contact';
  if (kind.startsWith('task_')) return 'task';
  if (kind.startsWith('note_')) return 'note';
  if (kind.startsWith('meeting_')) return 'meeting';
  if (kind.startsWith('document_')) return 'document';
  if (kind.includes('file') || kind.includes('attachment')) return 'file';
  return 'other';
}

export function CompanySidebar({
  company,
  contactsCount,
  deals,
  tasks,
  notes,
  history,
}: {
  company: CompanyDetail;
  contactsCount: number;
  deals: DealListItem[];
  tasks: TaskItem[];
  notes: NoteItem[];
  history: CompanyHistoryItem[];
}): ReactElement {
  const { t } = useTranslation();
  const openDeals = deals.filter(isDealOpen);
  const openDealsSum = openDeals.reduce((sum, deal) => sum + (Number(deal.amount) || 0), 0);
  const currency = openDeals[0]?.currency ?? 'RUB';
  const openTasks = tasks.filter(isTaskOpen);
  const taskCounts = `${openTasks.length} / ${openTasks.filter((task) => isTaskOverdue(task)).length}`;
  // Последняя известная активность — из уже загруженных данных: история компании
  // (с событиями её контактов, сделок и задач) и заметки. Наступивший срок — не
  // действие, его не считаем. Пусто — показываем дату обновления карточки.
  const candidates = [
    ...history
      .filter((item) => item.kind !== 'deadline_passed')
      .map((item) => ({ date: item.date, event: historyEvent(item.kind) })),
    ...notes.map((note) => ({ date: note.createdAt, event: 'note' })),
  ].filter((item) => Number.isFinite(Date.parse(item.date)));
  const last = candidates.sort((a, b) => Date.parse(b.date) - Date.parse(a.date))[0];
  const activityDate = last?.date ?? company.updatedAt;
  const activityLabel = last
    ? t(`crm.features.company_detail.event_${last.event}`)
    : t('crm.features.company_detail.updated');
  const website = company.website?.trim();
  const phone = company.phone?.trim();
  const optional = (key: string, label: string, value: ReactElement | string | undefined) =>
    value ? [{ key, label, value }] : [];
  return (
    <>
      <section className="entity-panel">
        <h2>{t('crm.features.company_detail.summary')}</h2>
        <div className="company-summary-grid">
          <EntityMetric
            testId="company-contacts-count"
            label={t('crm.features.company_detail.contacts')}
            value={contactsCount}
            exact={String(contactsCount)}
          />
          <EntityMetric
            testId="company-open-deals"
            label={t('crm.features.company_detail.open_deals')}
            value={openDeals.length}
            exact={`${openDeals.length} · ${formatMoney(openDealsSum, currency)}`}
            hint={formatMoney(openDealsSum, currency)}
            hintStyle={MONO_NUMERIC_STYLE}
          />
          <EntityMetric
            testId="company-activity"
            label={t('crm.features.company_detail.activity')}
            value={formatRelativeShort(activityDate)}
            exact={`${formatRelative(activityDate)} · ${formatDateTime(activityDate)}`}
            hint={`${activityLabel} · ${formatDate(activityDate)}`}
          />
          <EntityMetric
            testId="company-tasks"
            label={t('crm.features.company_detail.tasks')}
            labelTooltip={t('crm.features.company_detail.tasks_hint')}
            value={taskCounts}
            exact={taskCounts}
            hint={t('crm.deal_detail.task_counts_hint')}
          />
        </div>
      </section>
      <section className="entity-panel">
        <h2>{t('crm.features.company_detail.details')}</h2>
        <EntityFieldList
          fields={[
            ...optional(
              'industry',
              t('crm.features.company_detail.industry'),
              company.industry?.trim(),
            ),
            ...optional('domain', t('crm.features.company_detail.domain'), company.domain?.trim()),
            ...optional(
              'website',
              t('crm.features.company_detail.website'),
              website ? (
                <a href={companyWebsiteHref(website)} target="_blank" rel="noopener noreferrer">
                  {website}
                </a>
              ) : undefined,
            ),
            ...optional(
              'phone',
              t('crm.features.company_detail.phone'),
              phone ? <a href={`tel:${phone}`}>{phone}</a> : undefined,
            ),
            ...optional(
              'description',
              t('crm.features.company_detail.description'),
              company.description?.trim(),
            ),
            {
              key: 'created',
              label: t('crm.entity_detail.created'),
              value: formatDateTime(company.createdAt),
            },
            {
              key: 'updated',
              label: t('crm.entity_detail.updated'),
              value: formatDateTime(company.updatedAt),
            },
          ]}
        />
      </section>
    </>
  );
}
import { MONO_NUMERIC_STYLE } from '../../../shared/ui/list-styles.js';
